import { format, isSameDay, parseISO } from 'date-fns'
import humanizeString from 'humanize-string'
import React, { FC, ReactElement } from 'react'
import { includes } from 'lodash'
import { useParams } from 'react-router-dom'
import { TableDivider } from '../../../components/Common/TableDivider'
import { dividedDocumentsCategories } from '../../../constants'
import { Document, DocumentOrderBy } from '../../../graphql'
import { receivedPageStateVar, uploadDocsPageStateVar } from '../../../graphql/local'
import { usePageFiltersSorting } from '../../../hooks'
import { PATH_PARAMS } from '../../../routes/paths'
import { DocumentsTabName } from './CmDocumentsTabs'

export const DividerBySortDocs: FC<{
  documents: (Document | undefined)[] | undefined | null
  index: number
  children: ReactElement
}> = ({ documents, index, children }) => {
  const { [PATH_PARAMS.docsTab]: docsTab } = useParams() as Record<string, string>

  const pageFiltersStateVar =
    docsTab === DocumentsTabName.Received ? receivedPageStateVar : uploadDocsPageStateVar
  const { pageFilters } = usePageFiltersSorting(pageFiltersStateVar)
  const { sortBy } = pageFilters

  if (!documents || !includes(dividedDocumentsCategories, sortBy) || documents.length <= 5)
    return <>{children}</>

  const prevDocument = documents[index - 1]
  const currentDocument = documents[index]
  const nextDocument = documents[index + 1]

  const isNotLastRow = index + 1 !== documents.length

  const dividerFactory = {
    [DocumentOrderBy.Type]: () => ({
      isShowDivider: currentDocument?.type !== prevDocument?.type,
      isBorderBottomHidden: currentDocument?.type !== nextDocument?.type && isNotLastRow,
      dividerText: humanizeString(currentDocument?.type || ''),
    }),

    [DocumentOrderBy.CreatedAt]: () => ({
      isShowDivider: !isSameDay(
        new Date(currentDocument?.createdAt),
        new Date(prevDocument?.createdAt),
      ),
      isBorderBottomHidden:
        !isSameDay(new Date(currentDocument?.createdAt), new Date(nextDocument?.createdAt)) &&
        isNotLastRow,
      dividerText: format(parseISO(currentDocument?.createdAt), 'dd.MM.yyyy'),
    }),
  }

  const { isShowDivider, isBorderBottomHidden, dividerText } = dividerFactory[
    sortBy as keyof typeof dividerFactory
  ]()

  return (
    <>
      {isShowDivider && <TableDivider dividerText={dividerText} />}
      {React.cloneElement(children, { hideBorderBottom: isBorderBottomHidden })}
    </>
  )
}
